import { useRankingsQuery } from '../../api/queries';
import { makeHref } from '../../lib/format';
import RankingsGridMap from '../../components/RankingsGridMap';
import type { Franchisee, RankingKeyword } from '../../types';
import SubTabLoading from './SubTabLoading';

type Props = { franchisee: Franchisee };

const DISPLAY_LABELS = ['senior care', 'senior home care', 'home care', 'caregivers'];

function rankColor(rank: number | null): string {
  if (rank === null || rank === undefined) return '#c0392b';
  if (rank <= 3) return '#27ae60';
  if (rank <= 10) return '#2a5a8c';
  if (rank <= 20) return '#e67e22';
  return '#c0392b';
}

function rankText(rank: number | null): string {
  return rank !== null && rank !== undefined ? `#${rank}` : 'Not in top 100';
}

export default function RankingsSubTab({ franchisee }: Props) {
  const af = franchisee.af_number;
  const { data, isLoading, error } = useRankingsQuery(af);

  if (!franchisee.site_url) {
    return (
      <div className="tab-placeholder">
        <h3>No Site URL</h3>
        <p>This franchisee does not have a site URL configured.</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <SubTabLoading
        title="Loading Rankings Data..."
        sub="Checking Google search rankings via DataForSEO"
      />
    );
  }
  if (error) {
    return (
      <div className="tab-placeholder">
        <h3>Rankings Unavailable</h3>
        <p>{(error as Error).message}</p>
      </div>
    );
  }
  if (!data || !data.keywords || data.keywords.length === 0) {
    return (
      <div className="tab-placeholder">
        <h3>Rankings Unavailable</h3>
        <p>{data?.message ?? 'No rankings data yet.'}</p>
      </div>
    );
  }

  const marketLabel = data.market + (data.state ? `, ${data.state}` : '');
  const gmbLink = franchisee.gmb_link ?? '';
  const reviewsHref = gmbLink ? makeHref(gmbLink) : '';
  const qrSrc = reviewsHref
    ? `https://api.qrserver.com/v1/create-qr-code/?size=300x300&format=png&data=${encodeURIComponent(reviewsHref)}`
    : '';
  void qrSrc;

  return (
    <>
      {reviewsHref && (
        <div className="rankings-reviews-callout">
          <div className="rankings-reviews-callout-icon">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="#eab308">
              <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
            </svg>
          </div>
          <div className="rankings-reviews-callout-text">
            <strong>Not on the Maps 3-Pack for any search terms? You need to get more reviews!</strong>{' '}
            Here's your{' '}
            <a href={reviewsHref} target="_blank" rel="noopener noreferrer">
              reviews link
            </a>{' '}
            to share with clients and families. Also,{' '}
            <a href="/static/resources/12-proven-ways-to-improve-gmb.pdf" target="_blank" rel="noopener noreferrer">
              click here to download
            </a>{' '}
            a resource from AFI that can help you improve your GBP rankings.
          </div>
        </div>
      )}

      <div style={{ padding: '16px 20px 8px', color: '#1e3a5f', fontSize: 15, fontWeight: 600 }}>
        Google Search Rankings for{' '}
        <span style={{ color: '#2a5a8c' }}>{marketLabel}</span>
      </div>

      {data.h1 && (
        <div style={{ padding: '0 20px 12px', color: '#8a95a5', fontSize: 12 }}>
          Based on page title: &ldquo;{data.h1}&rdquo;
        </div>
      )}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 16,
          padding: '8px 20px 20px',
        }}
      >
        {data.keywords.map((kw, i) => (
          <KeywordCard key={kw.keyword} kw={kw} displayKeyword={DISPLAY_LABELS[i] ?? kw.keyword} />
        ))}
      </div>

      <div className="metrics-footer">
        Rankings data via DataForSEO • Cached for 24 hours • Queries run against Google US desktop results
      </div>

      <RankingsGridMap af={af} />
    </>
  );
}

function KeywordCard({ kw, displayKeyword }: { kw: RankingKeyword; displayKeyword: string }) {
  const orgColor = rankColor(kw.organicRank);
  const packColor = kw.inLocalPack ? '#27ae60' : '#c0392b';
  const packText = kw.inLocalPack ? 'Yes' : 'No';
  const packDetail = kw.inLocalPack && kw.localPackRank ? ` (#${kw.localPackRank} of 3)` : '';
  return (
    <div
      style={{
        background: '#fff',
        borderRadius: 10,
        padding: 20,
        boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
        border: '1px solid #e9ecef',
      }}
    >
      <div
        style={{
          fontSize: 14,
          fontWeight: 600,
          color: '#1e3a5f',
          marginBottom: 4,
          textTransform: 'capitalize',
        }}
      >
        {displayKeyword}
      </div>
      <div style={{ fontSize: 11, color: '#b0b8c4', marginBottom: 16 }}>
        Query: &ldquo;{kw.keyword}&rdquo;
      </div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 12,
          paddingBottom: 12,
          borderBottom: '1px solid #f0f2f5',
        }}
      >
        <div>
          <div
            style={{
              fontSize: 11,
              color: '#8a95a5',
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            Organic Rank
          </div>
          <div style={{ fontSize: 11, color: '#b0b8c4', marginTop: 2 }}>Google Search Results</div>
        </div>
        <div style={{ fontSize: 24, fontWeight: 700, color: orgColor }}>
          {rankText(kw.organicRank)}
        </div>
      </div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <div
            style={{
              fontSize: 11,
              color: '#8a95a5',
              fontWeight: 500,
              letterSpacing: 0.5,
              textTransform: 'uppercase',
            }}
          >
            Maps 3-Pack
          </div>
          <div style={{ fontSize: 11, color: '#b0b8c4', marginTop: 2 }}>Google Business Profile</div>
        </div>
        <div style={{ fontSize: 18, fontWeight: 700, color: packColor }}>
          {packText}
          {packDetail}
        </div>
      </div>
    </div>
  );
}
